import axios from "axios";
import { createContext, useContext, useEffect } from "react";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import { supabase } from "services/supabase";
import moment from "moment";
import React from "react";

const AxiosContext = createContext();

const API_URL = import.meta.env.VITE_API_URL;

// Create an instance of axios with a custom config
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    "Current-Datetime": moment().toISOString(),
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    return config;
  },
  (error) => {
    // Handle the request error
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to update the base URL
const setBaseURL = (url) => {
  axiosInstance.defaults.baseURL = url;
};

const getBaseURL = () => {
  return axiosInstance.defaults;
};

const AxiosProvider = ({ children }) => {
  const { currentUser } = useSupabaseAuth();

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          // Get the current session to ensure we have the latest token
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session?.access_token) {
            config.headers["Authorization"] = `Bearer ${session.access_token}`;
            // console.log('Token added to request:', moment().format());
          } else {
            // Remove Authorization header if no token
            delete config.headers["Authorization"];
          }

          window.axios = axiosInstance;
        } catch (error) {
          console.error("Failed to get auth token:", error);
          // Remove Authorization header on error
          delete config.headers["Authorization"];
        }

        return config;
      },
      (error) => {
        console.error("Request interceptor error:", error.message);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Try to refresh the token on 401 errors
          try {
            const {
              data: { session },
              error: refreshError,
            } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error("Token refresh failed:", refreshError);
              // You might want to redirect to login here
            }
          } catch (refreshError) {
            console.error("Failed to refresh token:", refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(interceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [currentUser]);

  return (
    <AxiosContext.Provider value={axiosInstance}>
      {children}
    </AxiosContext.Provider>
  );
};

const useAxios = () => {
  return useContext(AxiosContext);
};

export { axiosInstance, setBaseURL, getBaseURL, AxiosProvider, useAxios };
